import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"

export interface GanttRowItem {
  milestone: ProjectMilestone
  /** 1-based position in the full list, unchanged by grouping or collapsing. */
  number: number
  depth: 0 | 1
  isSection: boolean
  /** Lines that belong to this section (0 for a line or an ungrouped row). */
  childCount: number
  isCollapsed: boolean
}

// A section row carries a work-breakdown code whose every segment after the
// first is zeros: "01-0000", "12-000", "3.0". Its lines follow it in order.
const SECTION_CODE = /^\s*\d{1,3}(?:[-.]0+)+(?=\s|$)/

export const isSectionHeading = (name: string) => SECTION_CODE.test(name)

/**
 * The rows the Gantt shows, in the milestones' own order. Grouping needs no
 * parent field: a section's lines are the milestones between it and the next
 * section, which is how a contractor workbook is laid out.
 */
export function ganttRows(
  milestones: ProjectMilestone[],
  { grouped, collapsed }: { grouped: boolean; collapsed: Set<string> }
): { rows: GanttRowItem[]; hasSections: boolean } {
  const sectionOf: (string | null)[] = []
  const childCount = new Map<string, number>()
  let current: string | null = null
  milestones.forEach((milestone) => {
    if (isSectionHeading(milestone.name)) {
      current = milestone.id
      childCount.set(milestone.id, 0)
      sectionOf.push(null)
    } else {
      sectionOf.push(current)
      if (current) childCount.set(current, (childCount.get(current) ?? 0) + 1)
    }
  })
  const hasSections = childCount.size > 0

  const rows: GanttRowItem[] = []
  milestones.forEach((milestone, index) => {
    const section = grouped ? sectionOf[index] : null
    if (section && collapsed.has(section)) return
    const isSection = grouped && childCount.has(milestone.id)
    rows.push({
      milestone,
      number: index + 1,
      depth: section ? 1 : 0,
      isSection,
      childCount: isSection ? (childCount.get(milestone.id) ?? 0) : 0,
      isCollapsed: isSection && collapsed.has(milestone.id)
    })
  })
  return { rows, hasSections }
}
